import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, Skeleton, message, Divider, Typography } from 'antd';
import { PrinterOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import InvoicePrint from './InvoicePrint';

const { Text } = Typography;

const InvoicePrintModal = ({ visible, invoiceId, onCancel, paymentMethod, autoPrint, forceA4 }) => {
    const [invoice, setInvoice] = useState(null);
    const [company, setCompany] = useState(null);
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(false);
    const [readyToPrint, setReadyToPrint] = useState(false);

    const printRef = useRef(null);
    const iframeRef = useRef(null);
    const printTimerRef = useRef(null);

    // Initial Data Fetch
    useEffect(() => {
        if (visible && invoiceId) {
            fetchData();
            setReadyToPrint(false);
        } else {
            setInvoice(null);
            setReadyToPrint(false);
            if (printTimerRef.current) clearTimeout(printTimerRef.current);
        }
    }, [visible, invoiceId]);

    // Track when component is definitely rendered and ready
    useEffect(() => {
        if (!loading && invoice && company && branding && printRef.current && visible) {
            // Wait for next frame to ensure DOM is updated and template has switched
            const timer = setTimeout(() => {
                setReadyToPrint(true);
            }, 600); // Increased delay for template stability
            return () => clearTimeout(timer);
        }
    }, [loading, invoice, company, branding, visible]);

    // Handle Auto-Print trigger
    useEffect(() => {
        if (autoPrint && readyToPrint && visible) {
            handlePrint();
        }
    }, [autoPrint, readyToPrint, visible]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [invRes, compRes, brandRes] = await Promise.all([
                axios.get(`/api/invoices/${invoiceId}`, { headers }),
                axios.get('/api/company', { headers }).catch(() => ({ data: { name: 'MAKER AI POS', address: 'Amman, Jordan' } })),
                axios.get('/api/settings/invoice', { headers }).catch(() => ({ data: {} }))
            ]);
            setInvoice(invRes.data);
            setCompany(compRes.data);

            // Apply forceA4 override if requested
            const brandingData = brandRes.data;
            if (forceA4) {
                brandingData.paper_size = 'A4';
            }
            setBranding(brandingData);
        } catch (error) {
            console.error(error);
            message.error('Failed to load invoice details');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/invoices/${invoiceId}/document`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoice?.ref || 'invoice'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            message.error('Failed to download PDF');
        } finally {
            setLoading(false);
        }
    };

    const isThermalSetting = /80mm|57mm/.test(branding?.paper_size || '');
    const pageSize = isThermalSetting ? (/80mm/.test(branding?.paper_size) ? '80mm auto' : '57mm auto') : 'A4';
    const bodyWidth = isThermalSetting ? (/80mm/.test(branding?.paper_size) ? '80mm' : '57mm') : '210mm';

    const handlePrint = () => {
        // Critical safety check: ensures the DOM we are copying actually exists
        if (!iframeRef.current || !invoice || !printRef.current) {
            console.error("Print failed: Missing refs or data", { iframe: !!iframeRef.current, invoice: !!invoice, printArea: !!printRef.current });
            message.error("Print failed: Content not ready. Please try again.");
            return;
        }

        const renderedIsThermal = printRef.current.querySelector('[data-is-thermal="true"]') !== null;

        if (isThermalSetting && !renderedIsThermal) {
            console.warn("Template mismatch detected. Waiting for thermal layout to settle...");
            // If we are auto-printing, we might need to wait a bit longer or retry
            setTimeout(handlePrint, 300);
            return;
        }

        const content = printRef.current.innerHTML;
        const iframeDoc = iframeRef.current.contentWindow.document;

        // Capture all styles aggressively including Ant Design 5 CSS-in-JS
        const styleTags = Array.from(document.querySelectorAll('style')).map(s => s.cloneNode(true).outerHTML);
        const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.cloneNode(true).outerHTML);

        // Ensure Inter font is forced in the iframe
        const fontLink = '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap" rel="stylesheet">';

        const allStyles = [fontLink, ...linkTags, ...styleTags].join('\n');

        const isThermal = branding?.paper_size === '80mm' || branding?.paper_size === '57mm';
        const pageSize = isThermal ? (branding?.paper_size === '57mm' ? '57mm auto' : '80mm auto') : 'A4';
        const bodyWidth = isThermal ? (branding?.paper_size === '57mm' ? '57mm' : '80mm') : 'auto';

        iframeDoc.open();
        iframeDoc.write(`
            <!DOCTYPE html>
            <html lang="en">
                <head>
                    <title>Invoice ${invoice.ref}</title>
                    <meta charset="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    ${allStyles}
                    <style>
                        * { box-sizing: border-box !important; }
                        body { 
                            margin: 0 !important; 
                            padding: 0 !important; 
                            background: #fff !important; 
                            -webkit-print-color-adjust: exact !important; 
                            print-color-adjust: exact !important;
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                        }
                        #print-wrapper {
                            width: 100% !important;
                        }
                        @media print {
                            @page { 
                                size: ${pageSize} !important; 
                                margin: 0 !important; 
                            }
                            body { 
                                width: ${bodyWidth} !important;
                                margin: 0 !important; 
                                padding: 0 !important;
                            }
                            #print-wrapper {
                                width: ${bodyWidth} !important;
                                margin: 0 !important;
                                padding: 0 !important;
                            }
                            .no-print { display: none !important; }
                        }
                    </style>
                </head>
                <body>
                    <div id="print-wrapper">
                        ${content}
                    </div>
                    <script>
                        // Wait for all resources (images/fonts) before printing
                        window.onload = function() {
                            // Extra safety timeout for layout settling
                            setTimeout(function() {
                                window.focus();
                                window.print();
                                // We don't close the modal here automatically to let user see status
                            }, 800);
                        };
                    </script>
                </body>
            </html>
        `);
        iframeDoc.close();
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{invoice ? `Print Invoice: ${invoice.ref}` : 'Preparing Invoice...'}</span>
                    {loading && <Skeleton.Button active size="small" shape="round" style={{ width: 100 }} />}
                    {readyToPrint && <Text type="success" style={{ fontSize: '12px' }}>● Ready</Text>}
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={950}
            centered
            footer={[
                <Button key="close" icon={<CloseOutlined />} onClick={onCancel}>
                    Close
                </Button>,
                <Button key="download" icon={<DownloadOutlined />} onClick={handleDownload} disabled={loading || !invoice}>
                    Download PDF
                </Button>,
                <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!readyToPrint}>
                    Print
                </Button>
            ]}
            style={{ top: 20 }}
            bodyStyle={{ padding: 0, height: '80vh', overflowY: 'auto', background: '#f0f2f5' }}
        >
            {loading && !invoice ? (
                <div style={{ padding: '60px', background: '#fff', textAlign: 'center', height: '100%' }}>
                    <Skeleton active paragraph={{ rows: 12 }} />
                    <div style={{ marginTop: 24, fontSize: '16px', color: '#1890ff' }}>Fetching invoice details and branding...</div>
                </div>
            ) : invoice ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
                    <div
                        ref={printRef}
                        style={{
                            width: isThermalSetting ? (/80mm/.test(branding?.paper_size) ? '80mm' : '57mm') : '210mm',
                            margin: '0 auto',
                            backgroundColor: '#fff',
                            minHeight: isThermalSetting ? 'auto' : '297mm',
                            padding: isThermalSetting ? '0' : '0',
                            boxShadow: '0 0 30px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <InvoicePrint invoice={invoice} company={company} branding={branding} paymentMethod={paymentMethod} />
                    </div>
                </div>
            ) : (
                <div style={{ padding: '100px', textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '18px' }}>Unable to load invoice. Please try again.</Text>
                </div>
            )}

            <iframe
                ref={iframeRef}
                style={{ position: 'absolute', width: 0, height: 0, border: 'none', visibility: 'hidden', pointerEvents: 'none' }}
                title="print-frame"
                tabIndex="-1"
            />
        </Modal>
    );
};

export default InvoicePrintModal;
